import React, { useState } from 'react';
import { Product } from '../types';
import { X, Check, Play, Maximize2, Cpu, Shield, ArrowRight, Box, Sparkles, Clock } from 'lucide-react';
import { BlurImage } from './ui/BlurImage';
import { Lightbox } from './ui/Lightbox';
import { MarkdownRenderer } from './ui/MarkdownEditor'; 
import { useLanguage } from '../services/languageContext';
import { useTranslatedText } from '../hooks/useTranslatedText';
import { normalizeVideoUrl, isEmbeddable, isVideoUrl } from '../services/videoUtils';
import { SaleTimer } from './ui/SaleTimer';

interface ProductModalProps {
  product: Product;
  onClose: () => void;
  onAddToCart: () => void;
}

export const ProductModal: React.FC<ProductModalProps> = ({ product, onClose, onAddToCart }) => {
  const { t } = useLanguage();
  const translatedTitle = useTranslatedText(product.title, 'ru');
  const translatedDesc = useTranslatedText(product.description, 'ru');
  const allImages = [product.coverImage, ...(product.gallery || [])].filter(Boolean);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const currentMediaUrl = normalizeVideoUrl(allImages[activeMediaIndex]);
  const isVideo = isVideoUrl(currentMediaUrl);
  const isDriveEmbed = isVideo ? isEmbeddable(currentMediaUrl) : false;

  const isLegacyDemoVideo = activeMediaIndex === 0 && product.demoVideoUrl;
  const activeVideoUrl = isLegacyDemoVideo ? normalizeVideoUrl(product.demoVideoUrl) : (isVideo ? currentMediaUrl : null);
  
  const showVideoPlayer = (isLegacyDemoVideo && isPlaying) || (isVideo && activeMediaIndex > 0) || (isVideo && !product.demoVideoUrl && isPlaying);

  const handleMediaClick = (index: number) => {
    setActiveMediaIndex(index);
    setIsPlaying(false);
  };

  const isSaleValid = product.isOnSale && (!product.saleEndsAt || new Date(product.saleEndsAt) > new Date());

  const discount = isSaleValid && product.originalPrice 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) 
    : 0;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-end lg:items-center justify-center lg:p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-[#000]/95 backdrop-blur-xl animate-fade-in" onClick={onClose} />
        
        {/* Main Modal Window */}
        <div className="relative w-full max-w-[1400px] h-[100dvh] max-h-[100dvh] sm:h-[95vh] sm:max-h-[95vh] lg:h-[90vh] lg:max-h-[90vh] bg-[#020203] border-t lg:border border-white/10 rounded-t-3xl lg:rounded-2xl overflow-hidden flex flex-col lg:flex-row animate-slide-up shadow-2xl ring-1 ring-white/5">
          
          {/* Close Button */}
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 z-50 w-10 h-10 bg-black/60 backdrop-blur border border-white/10 rounded-full text-white hover:bg-white hover:text-black transition-all duration-300 flex items-center justify-center group"
          >
            <X size={20} className="group-hover:rotate-90 transition-transform" />
          </button>

          {/* --- LEFT COLUMN: MEDIA --- */}
          <div className="w-full lg:w-[60%] h-[min(42dvh,320px)] sm:h-[40vh] lg:h-full flex flex-col bg-black relative border-b lg:border-b-0 lg:border-r border-white/5 group/media shrink-0 min-h-0">
             
             <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-black">
               {showVideoPlayer && activeVideoUrl ? (
                  isDriveEmbed || (isLegacyDemoVideo && isEmbeddable(activeVideoUrl)) ? (
                      <iframe 
                        src={activeVideoUrl} 
                        className="w-full h-full min-h-[200px] object-contain relative z-10 border-0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                        title="Product video"
                      />
                  ) : (
                      <video
                        src={activeVideoUrl}
                        autoPlay
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full max-h-[50dvh] sm:max-h-none object-contain relative z-10 bg-black"
                      />
                  )
               ) : (
                 <div className="relative w-full h-full flex items-center justify-center bg-black">
                   <div className="w-full h-full relative bg-black flex items-center justify-center">
                      {isVideo ? (
                         <div className="w-full h-full flex items-center justify-center bg-gray-900 relative">
                            {isDriveEmbed ? (
                               <div className="w-full h-full flex items-center justify-center text-gray-500"><Play size={40} /></div>
                            ) : (
                                <video key={currentMediaUrl} src={currentMediaUrl} className="w-full h-full object-contain opacity-80" muted autoPlay loop playsInline preload="metadata" />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center z-20">
                                <button onClick={(e) => { e.stopPropagation(); setIsPlaying(true); }} className="w-20 h-20 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-xl rounded-full flex items-center justify-center transition-all duration-300 group/play">
                                  <Play fill="white" className="ml-1 text-white group-hover/play:scale-110 transition-transform" size={32} />
                                </button>
                            </div>
                         </div>
                      ) : (
                        <BlurImage 
                            src={allImages[activeMediaIndex]} 
                            alt={product.title} 
                            priority={true} 
                            className={`w-full h-full object-contain relative z-10 transition-transform duration-700 ${product.is18Plus ? 'blur-2xl lg:blur-3xl group-hover/media:blur-0' : ''}`}
                            onClick={() => setLightboxOpen(true)}
                        />
                      )}
                   </div>
                   
                   {isLegacyDemoVideo && !isPlaying && !isVideo && (
                     <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                       <button onClick={(e) => { e.stopPropagation(); setIsPlaying(true); }} className="pointer-events-auto w-16 h-16 lg:w-20 lg:h-20 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-xl rounded-full flex items-center justify-center transition-all duration-300 group/play">
                         <Play fill="white" className="ml-1 text-white group-hover/play:scale-110 transition-transform" size={24} />
                       </button>
                     </div>
                   )}
                 </div>
               )}

               {/* Status Tags */}
               <div className="absolute top-4 left-4 z-20 flex gap-2 pointer-events-none">
                  {discount > 0 && (
                    <span className="px-3 py-1 bg-red-500 text-black text-[10px] font-bold uppercase tracking-wider rounded">
                      {t('prod_save')} {discount}%
                    </span>
                  )}
                  {product.isNew && (
                    <span className="px-3 py-1 bg-cyan-500 text-black text-[10px] font-bold uppercase tracking-wider rounded flex items-center gap-1 shadow-[0_0_15px_rgba(6,182,212,0.5)]">
                      <Sparkles size={10} className="fill-black" /> NEW
                    </span>
                  )}
               </div>

               <button onClick={() => setLightboxOpen(true)} className="absolute bottom-4 right-4 z-20 p-2 text-gray-400 hover:text-white transition-colors opacity-70 hover:opacity-100">
                 <Maximize2 size={20} />
               </button>
             </div>

             {allImages.length > 1 && (
               <div className="h-16 lg:h-20 bg-[#050505] border-t border-white/10 flex items-center px-4 lg:px-6 gap-3 overflow-x-auto hide-scrollbar shrink-0">
                  {allImages.map((img, idx) => {
                    const cleanImgUrl = normalizeVideoUrl(img);
                    const isThumbVideo = isVideoUrl(cleanImgUrl);
                    return (
                    <button 
                      key={idx} 
                      onClick={() => handleMediaClick(idx)}
                      className={`relative w-10 h-10 lg:w-12 lg:h-12 rounded-lg overflow-hidden border transition-all duration-300 flex-shrink-0 group/thumb ${activeMediaIndex === idx ? 'border-white opacity-100 ring-1 ring-white' : 'border-white/10 opacity-40 hover:opacity-100'}`}
                    >
                      {isThumbVideo ? (
                         <div className="w-full h-full bg-gray-900 flex items-center justify-center overflow-hidden">
                            <video src={cleanImgUrl} className="w-full h-full object-cover opacity-70 group-hover/thumb:opacity-100 transition-opacity" muted autoPlay loop playsInline preload="metadata" />
                            <div className="absolute inset-0 flex items-center justify-center"><Play size={10} className="text-white fill-white shadow-black drop-shadow-md" /></div>
                         </div>
                      ) : (
                         <BlurImage src={img} alt="" className="w-full h-full object-cover" priority={false} />
                      )}
                    </button>
                  )})}
               </div>
             )}
          </div>

          {/* --- RIGHT COLUMN: INFO & ACTIONS --- */}
          <div className="w-full lg:w-[40%] flex-1 lg:h-full bg-[#050505] flex flex-col relative overflow-hidden">
            
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-10 pb-24 lg:pb-10">
              
              <div className="flex items-center gap-2 mb-4 lg:mb-6">
                 <Box size={14} className="text-gray-500"/>
                 <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">{t('prod_breadcrumb')} / {product.categories[0]}</span>
              </div>

              <h1 className="text-2xl lg:text-4xl font-display font-black text-white mb-4 lg:mb-6 leading-[1.1] uppercase tracking-tight">
                {translatedTitle}
              </h1>

              {/* ⚡️ LARGE TIMER IN MODAL */}
              {isSaleValid && product.saleEndsAt && (
                  <div className="mb-6 bg-red-900/10 border border-red-500/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                          <Clock className="text-red-500" size={16} />
                          <span className="text-[10px] font-bold uppercase tracking-widest text-red-400">Offer Expires In</span>
                      </div>
                      <SaleTimer deadline={product.saleEndsAt} size="lg" />
                  </div>
              )}

              <div className="flex items-end gap-4 mb-6 lg:mb-8 border-b border-white/10 pb-6 lg:pb-8">
                 <span className="text-3xl lg:text-4xl font-mono font-bold text-white tracking-tighter">${product.price}</span>
                 {isSaleValid && product.originalPrice && (
                   <div className="flex flex-col mb-1">
                      <span className="text-sm text-gray-500 line-through decoration-red-500 decoration-1">${product.originalPrice}</span>
                      <span className="text-[10px] text-green-500 font-bold uppercase tracking-wider">{t('prod_discount_active')}</span>
                   </div>
                 )}
              </div>

              <div className="mb-8 lg:mb-10">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-4">{t('prod_sys_desc')}</h3>
                <div className="text-sm font-light text-gray-300"><MarkdownRenderer content={translatedDesc} /></div>
              </div>

              <div className="mb-8 lg:mb-10 p-5 rounded-xl bg-white/[0.02] border border-white/5">
                 <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-4 flex items-center gap-2"><Cpu size={14} /> {t('prod_tech_specs')}</h3>
                 <div className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="mt-1 w-1 h-1 rounded-full bg-indigo-500 shrink-0" />
                        <span className="text-xs text-gray-300 font-mono">{feature}</span>
                      </div>
                    ))}
                 </div>
              </div>

              <div className="mb-8 lg:mb-10">
                 <div className="flex flex-wrap gap-2">
                    {product.tags && product.tags.map((tag, idx) => (
                       <span key={idx} className="text-[10px] uppercase font-bold text-gray-500 border border-white/10 px-2 py-1 rounded hover:text-white hover:border-white/30 transition-colors cursor-default">#{tag}</span>
                    ))}
                 </div>
              </div>
            </div>

            <div className="absolute lg:relative bottom-0 left-0 right-0 p-4 lg:p-8 bg-[#020203] border-t border-white/10 z-20 pb-safe">
               <div className="hidden lg:flex items-center justify-between text-[10px] text-gray-600 uppercase tracking-widest font-bold mb-4">
                  <span className="flex items-center gap-2"><Shield size={12}/> {t('prod_secure')}</span>
                  <span className="flex items-center gap-2"><Check size={12} className="text-green-500"/> {t('prod_instant')}</span>
               </div>
               
               <button onClick={onAddToCart} className="w-full group bg-white hover:bg-gray-200 text-black h-12 lg:h-14 rounded-xl font-display font-black text-sm uppercase tracking-widest flex items-center justify-between px-6 transition-all active:scale-[0.98]">
                 <span className="flex items-center gap-3">{t('prod_add_cart')}</span>
                 <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
               </button>
            </div>

          </div>
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox 
          src={activeVideoUrl || allImages[activeMediaIndex]} 
          type={showVideoPlayer ? 'video' : 'image'} 
          onClose={() => setLightboxOpen(false)} 
        />
      )}
    </>
  );
};